import { createTheme } from "@mui/material/styles";

export const getTheme = (mode = "light") => {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      text: {
        primary: "#252525",
        secondary: "#4C4C4C",
        contrast: "#FFFFFF",
        gradient: "linear-gradient(180deg, #DEA829 0%, #FE8888 100%)",
      },
      primary: {
        main: "#F7A86E",
        contrastText: "#FFFFFF",
      },
      background: {
        default: isDark ? "#121212" : "#FFFFFF",
        paper: isDark ? "#1E1E1E" : "#FFFFFF",
      },
      success: {
        main: "#4CAF50",
        contrastText: "#FFFFFF",
      },
      error: {
        main: "#EF5350",
        contrastText: "#FFFFFF",
      },
      button: {
        gradient: "linear-gradient(90deg, #E2A532 0%, #F79072 100%)",
        contrast: "#FFFFFF",
      },
      border: {
        gradient: "#F7A86E",
      },
      icon: {
        gradient: "#F7A86E",
      },
      tab: {
        gradient: "linear-gradient(180deg, #DEA829 0%, #FE8888 100%)",
      },
      gradients: {
        sidebar:
          "linear-gradient(180deg, rgba(229, 172, 79, 0.45) 0%, rgba(253, 135, 136, 0.45) 100%)",
        sidebarButton: "linear-gradient(90deg, #D8A630 0%, #FC8A82 100%)",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@media (max-width: 1024.95px)": {
            "body.laundry-scope .MuiInputLabel-root, body.laundry-scope .MuiFormLabel-root, body.laundry-scope .MuiFormHelperText-root, body.laundry-scope .MuiInputBase-input, body.laundry-scope .MuiSelect-select, body.laundry-scope .MuiMenuItem-root, body.laundry-scope .MuiTableCell-root, body.laundry-scope .MuiChip-label, body.laundry-scope .MuiTab-root, body.laundry-scope .MuiButton-root, body.laundry-scope .MuiTypography-caption, body.laundry-scope .MuiTypography-body1, body.laundry-scope .MuiTypography-body2, body.laundry-scope .MuiTablePagination-root, body.laundry-scope .MuiTablePagination-selectLabel, body.laundry-scope .MuiTablePagination-displayedRows":
              {
                fontSize: "0.8125rem !important",
              },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "@media (max-width: 1024.95px)": {
              "body.laundry-scope &": {
                minWidth: 44,
                minHeight: 44,
              },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            "@media (max-width: 1024.95px)": {
              "body.laundry-scope &": {
                minHeight: 44,
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "@media (max-width: 1024.95px)": {
              "body.laundry-scope &": {
                minHeight: 44,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#F7A86E",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // background: "linear-gradient(180deg, #DEA829 0%, #FE8888 100%)",
            // WebkitBackgroundClip: "text",
            // WebkitTextFillColor: "transparent",
            "&.Mui-focused": {
              background: "linear-gradient(180deg, #DEA829 0%, #FE8888 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            "@media (max-width: 1024.95px)": {
              "body.laundry-scope &": {
                minHeight: 44,
              },
            },
          },
          contained: {
            background: "linear-gradient(90deg, #E2A532 0%, #F79072 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(90deg, #c98c1c, #f56b6b)",
            },
          },
          outlined: {
            border: "1px solid #F7A86E",
            background:
              "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, #DEA829, #FE8888) border-box",
            background: "linear-gradient(180deg, #DEA829 0%, #FE8888 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: "#F7A86E",
            "&.Mui-checked": {
              color: "#F7A86E",
            },
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: "#F7A86E",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: "#F7A86E",
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: "#F7A86E",
            "&.Mui-checked": {
              color: "#F7A86E",
            },
          },
        },
      },
      "& .MuiBadge-badge": {
        color: "#DFA828",
        backgroundColor: "#ECE4C7",
        fontSize: { xs: 12, sm: "1.44vw", md: "0.83vw" },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: "#ECE4C7",
            color: "#F7A86E",
          },
        },
      },
    },
    typography: {
      fontFamily: "'Poppins','Inter', sans-serif",
      htmlFontSize: 16,
      h1: { fontWeight: 700, fontSize: "2.5rem", lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: "2rem", lineHeight: 1.3 },
      h3: { fontWeight: 600, fontSize: "1.75rem", lineHeight: 1.4 },
      h4: { fontWeight: 600, fontSize: "1.5rem", lineHeight: 1.4 },
      h5: { fontWeight: 500, fontSize: "1.25rem", lineHeight: 1.5 },
      h6: { fontWeight: 500, fontSize: "1.125rem", lineHeight: 1.5 },
      subtitle1: { fontSize: "1rem", fontWeight: 400 },
      subtitle2: { fontSize: "0.875rem", fontWeight: 400 },
      body1: { fontSize: "1rem", fontWeight: 400 },
      body2: { fontSize: "0.875rem", fontWeight: 400 },
      button: {
        fontSize: "0.875rem",
        fontWeight: 600,
        textTransform: "uppercase",
      },
      caption: { fontSize: "0.75rem", fontWeight: 400 },
      overline: {
        fontSize: "0.75rem",
        fontWeight: 500,
        textTransform: "uppercase",
      },
    },
  });
};
